import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectOriginDTOSubtype,
    selectVisitorDisplayName,
} from 'flow/selectors/TicketsSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import { TelephoneNumber } from 'flow/types/TelephoneNumber';
import { TICKET_ORIGIN } from 'flow/utils/ticket-channel-utils';
import ContactTitle from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/ContactTitle';

interface Props {
    ticketKey: TicketKey;
    contact?: TypedMap<ContactFull>;
    contactFullName: string;
    headerNumber?: TypedMap<TelephoneNumber>;
}

/**
 * Title row of the contact summary card — normally the shared contact title
 * (name / company / number). When there is no name, company or number to
 * show, the visitor's own identifier is the title instead of a blank header:
 * the email address on an email ticket, otherwise whatever the ticket knows
 * the visitor as (`visitorDisplayName`), as the old contact header did.
 */
const ContactCardTitle = ({
    ticketKey,
    contact,
    contactFullName,
    headerNumber,
}: Props) => {
    const originIsEmail =
        useAppSelector((state) => selectOriginDTOSubtype(state, ticketKey)) ===
        TICKET_ORIGIN.EMAIL;
    const visitorDisplayName = useAppSelector((state) =>
        selectVisitorDisplayName(state, ticketKey),
    );

    // Email visitors carry their address on the ticket origin
    // (`visitorDisplayName`) when no contact matched; other channels carry
    // whatever the visitor is known as there.
    const fallbackTitle =
        (originIsEmail ? contact?.get('email') : undefined) ||
        visitorDisplayName;
    const companyName = contact?.get('companyName');
    const showFallbackTitle =
        !contactFullName && !companyName && !headerNumber && !!fallbackTitle;

    return (
        // Flex row so ContactTitle's inner name can shrink and scroll (ellipsis in
        // browsers without text-fit) while its type icon — `shrink-0` — keeps its
        // space, instead of the whole inline-flex overflowing and getting
        // clipped icon-first.
        <span className="text-fit-shrink flex min-w-0 items-center text-xl leading-8 font-bold">
            {showFallbackTitle ? (
                <span className="min-w-0 truncate">{fallbackTitle}</span>
            ) : (
                <ContactTitle
                    contact={contact}
                    contactFullName={contactFullName}
                    number={headerNumber}
                />
            )}
        </span>
    );
};

export default React.memo(ContactCardTitle);
